import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Card, Descriptions, Popconfirm, Space, Tag, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from 'utils/formatDate';

const { Text } = Typography;

// decoded_payload is stored as a JSON string (marshalDecodedPayload, backend
// webhook_service.go) - pretty-print for readability, but reconciliation-
// triggered rows (notification_type "RECONCILE") have no real payload to
// decode (raw_payload is just "{}"), so decoded_payload is legitimately empty
// there - fall back to raw_payload rather than showing nothing.
const formatDecodedPayload = (item: { decoded_payload?: string; raw_payload?: string }) => {
  const source = item.decoded_payload || item.raw_payload || '';
  try {
    return JSON.stringify(JSON.parse(source), null, 2);
  } catch {
    return source;
  }
};

const PROVIDER_COLORS: Record<string, string> = {
  apple: 'default',
  google: 'green',
};

const STATUS_COLORS: Record<string, string> = {
  received: 'blue',
  processed: 'green',
  failed: 'red',
  skipped_duplicate: 'default',
};

// Same values/colors as iap-notifications/config.tsx's ENVIRONMENT_COLORS.
const ENVIRONMENT_COLORS: Record<string, string> = {
  Sandbox: 'orange',
  Production: 'green',
};

// DeleteButton is blocked server-side for rows confirmed Production (see
// IapNotificationService.Delete) - this confirmation dialog surfaces the
// row's own Environment so an operator can judge an ambiguous/legacy row
// (empty Environment) the server can't classify for itself.
const DeleteButton = ({ item, apiCall }) => {
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    mutationFn: () => apiCall.delete.queryFn({ id: item.id }),
    onSuccess: () => {
      message.success('Notification deleted.');
      navigate('/investment/iap-notifications');
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to delete notification');
    },
  });

  return (
    <Popconfirm
      title="Delete this notification?"
      description={
        <>
          Environment: <b>{item.environment || 'unknown'}</b>. This cannot be
          undone. Production rows are rejected by the server regardless of
          this confirmation.
        </>
      }
      onConfirm={() => mutate()}
      okText="Delete"
      okButtonProps={{ danger: true }}
    >
      <Button danger loading={isPending}>
        Delete
      </Button>
    </Popconfirm>
  );
};

const ItemDetail = ({ item, apiCall }) => {
  return (
    <Card
      extra={
        <Space>
          <DeleteButton item={item} apiCall={apiCall} />
        </Space>
      }
    >
      <Descriptions column={1} bordered>
        <Descriptions.Item label="UUID">{item.id}</Descriptions.Item>
        <Descriptions.Item label="Provider">
          <Tag color={PROVIDER_COLORS[item.provider]}>{item.provider}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Notification Type">
          {item.notification_type}
        </Descriptions.Item>
        <Descriptions.Item label="External ID">
          {item.external_id}
        </Descriptions.Item>
        <Descriptions.Item label="Environment">
          {item.environment ? (
            <Tag color={ENVIRONMENT_COLORS[item.environment] ?? 'default'}>
              {item.environment}
            </Tag>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={STATUS_COLORS[item.status]}>{item.status}</Tag>
        </Descriptions.Item>
        {item.error_message && (
          <Descriptions.Item label="Error">
            <Alert message={item.error_message} type="error" />
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Subscriber">
          {item.subscription_user?.user?.email ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Subscription User ID">
          {item.subscription_user_id ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Subscription Transaction ID">
          {item.subscription_transaction_id ?? '-'}
        </Descriptions.Item>
        {item.subscription_transaction && (
          <>
            <Descriptions.Item label="Amount (list price)">
              {item.subscription_transaction.amount}
            </Descriptions.Item>
            <Descriptions.Item label="Amount Charged">
              {item.subscription_transaction.amount_charged == null
                ? 'Not yet backfilled'
                : item.subscription_transaction.amount_charged}
            </Descriptions.Item>
            <Descriptions.Item label="Trial">
              {item.subscription_transaction.is_trial ? (
                <Tag color="blue">Free Trial - no money collected</Tag>
              ) : (
                <Tag color="green">Charged</Tag>
              )}
            </Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="Decoded Payload">
          <Text code copyable style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {formatDecodedPayload(item)}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="Raw Payload">
          <Text code copyable style={{ wordBreak: 'break-all' }}>
            {item.raw_payload}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="Received At">
          {formatDateTime(item.created_at)}
        </Descriptions.Item>
        <Descriptions.Item label="Updated At">
          {formatDateTime(item.updated_at)}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

export default ItemDetail;
